/* eslint-disable import/prefer-default-export */
import * as React from 'react';
import { useSelector } from 'react-redux';
import { PropertyByPermission, UserPermissions } from '@centecomm/portal-service';

import { PropertyTenantResolution } from '../../../State';
import { usePropertyTenantResolution } from '../usePropertyTenantResolution';

type SinglePropertySelectionHook = (
    permission: UserPermissions,
    getAssociatedPropertiesOnly?: boolean,
    disableOccupantFetch?: boolean,
) => {
    availableProperties: PropertyByPermission[];
    selectedPropertyId: number;
    visibleProperties: number[];
    selectProperty: (propertyId: number | string) => void;
    searchProperties: (searchTerm: string) => void;
    propertiesAreLoaded: boolean;
    hasSingleProperty: boolean;
};

const { selectors } = PropertyTenantResolution;
export const useSinglePropertySelection: SinglePropertySelectionHook = (
    permission: UserPermissions,
    getAssociatedPropertiesOnly?: boolean,
    disableOccupantFetch?: boolean,
) => {
    const [propertiesAreLoaded, loadOccupants] = usePropertyTenantResolution(permission, false, getAssociatedPropertiesOnly);

    const availableProperties: PropertyByPermission[] = useSelector(selectors.sortedPropertiesList);

    const [selectedPropertyId, setSelectedProperty] = React.useState<number>(-1);
    const [visibleProperties, setVisibleProperties] = React.useState<number[]>([]);
    const [hasSingleProperty, setHasSingleProperty] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (propertiesAreLoaded && availableProperties?.length === 1) {
            setHasSingleProperty(true);
            setSelectedProperty(availableProperties[0].id);
            loadOccupants(+availableProperties[0].id);
        } else {
            // Soft reset
            setHasSingleProperty(false);
        }
    }, [propertiesAreLoaded, JSON.stringify(availableProperties)]);

    const selectProperty = (propertyId: number | string): void => {
        const id = typeof propertyId === 'string' ? parseInt(propertyId, 10) : propertyId;

        if (!disableOccupantFetch) loadOccupants(+propertyId);

        setSelectedProperty(id);
    };

    const searchProperties = (searchTerm: string) => {
        if (!searchTerm) {
            setVisibleProperties([]);
        }

        const term = searchTerm.toLowerCase();

        const filteredList: number[] = availableProperties
            .filter((p: PropertyByPermission) => p.name.toLowerCase().includes(term))
            .map((p) => p.id);

        setVisibleProperties(filteredList);
    };

    return {
        availableProperties,
        selectedPropertyId,
        visibleProperties,
        selectProperty,
        searchProperties,
        propertiesAreLoaded,
        hasSingleProperty,
    };
};
